import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, unref as _unref, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "sidebar-content" }
const _hoisted_2 = {
  key: 0,
  class: "sidebar-mobile-header"
}
const _hoisted_3 = ["onClick"]
const _hoisted_4 = { class: "sidebar-menu" }

import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/admin/authStore';
import { usePermissaoStore } from '@/stores/admin/permissaoStore';
import { useI18n } from 'vue-i18n';

interface MenuItem {
  nome: string
  path: string
  icon: string
  secao: string
  modulo?: string
  i18nKey?: string
}

interface MenuSection {
  id: string
  label: string
  icon: string
  i18nKey?: string
  items: MenuItem[]
}


export default /*@__PURE__*/_defineComponent({
  __name: 'Sidebar',
  props: {
    collapsed: { type: Boolean },
    open: { type: Boolean }
  },
  emits: ['close'],
  setup(__props: any, { emit: __emit }) {

const { t } = useI18n();

const props = __props

const emit = __emit
const route = useRoute()

// Accordion — secoes abertas/fechadas
const secoesAbertas = reactive<Record<string, boolean>>({})

const toggleSecao = (id: string) => {
  secoesAbertas[id] = !secoesAbertas[id]
}

// Abrir automaticamente a secao que contem a rota ativa
const abrirSecaoAtiva = () => {
  const path = route.path
  for (const secao of secoesVisiveis.value) {
    if (secao.items.some(item => item.path === path)) {
      secoesAbertas[secao.id] = true
      return
    }
  }
}

const onLinkClick = () => {
  if (window.innerWidth < 640) {
    emit('close')
  }
}

const authStore = useAuthStore();
const permissaoStore = usePermissaoStore();

const secaoConfig: { id: string; label: string; icon: string; i18nKey?: string }[] = [
  { id: 'loja', label: 'Loja', icon: 'fas fa-store', i18nKey: 'nav.loja' },
  { id: 'estoque', label: 'Produtos & Estoque', icon: 'fas fa-boxes' },
  { id: 'financeiro', label: 'Financeiro', icon: 'fas fa-dollar-sign' },
  { id: 'gerencia', label: 'Gerencia', icon: 'fas fa-chart-line' },
  { id: 'servicos', label: 'Servicos', icon: 'fas fa-calendar-check' },
  { id: 'pet', label: 'Pet', icon: 'fas fa-paw' },
  { id: 'restaurante', label: 'Restaurante', icon: 'fas fa-utensils' },
  { id: 'veterinaria', label: 'Veterinaria', icon: 'fas fa-stethoscope' },
  { id: 'otica', label: 'Otica', icon: 'fas fa-glasses' },
  { id: 'admin', label: 'Administrador', icon: 'fas fa-shield-alt', i18nKey: 'nav.administracao' },
  { id: 'usuario', label: 'Minha Conta', icon: 'fas fa-user-cog' },
];

/**
 * Logica simplificada de visibilidade:
 * 1. Backend retorna TODAS as paginas ativas (tb_pagina) via permissaoStore
 * 2. modulosVisiveis (calculado por segmento+plano da loja) filtra quais aparecem
 * 3. Admin ve tudo — sem filtro
 * 4. Nao precisa mais de tb_perfil_permissao para controlar menu
 */
const resolvedItems = computed<MenuItem[]>(() => {
  const allPages = permissaoStore.menuItems || [];
  const modulosVisiveis = authStore.modulosVisiveis || [];

  // Converter para MenuItem, excluir blocos de dashboard
  const pages: MenuItem[] = allPages
    .filter(i => !i.path.startsWith('dashboard:'))
    .map(i => ({
      nome: i.nome,
      path: i.path,
      icon: i.icon,
      secao: i.secao || 'loja',
    }));

  // Admin ve tudo (mesma definicao de admin usada pelo guard de rota e pelo backend)
  if (authStore.isAdmin) {
    return pages;
  }

  // Filtrar por segmento+plano (modulosVisiveis)
  // A pagina aparece se:
  // - sua rota esta em modulosVisiveis, OU
  // - e uma pagina sem modulo guard (sempre visivel, como /home, /meu-perfil)
  if (modulosVisiveis.length > 0) {
    return pages.filter(item => modulosVisiveis.includes(item.path));
  }

  // Se modulosVisiveis estiver vazio (loja nao configurada), mostrar apenas comuns
  return pages.filter(item => ['/home', '/meu-perfil', '/lgpd'].includes(item.path));
});

const secoesVisiveis = computed<MenuSection[]>(() => {
  return secaoConfig
    .map(s => ({
      ...s,
      items: resolvedItems.value.filter(i => i.secao === s.id),
    }))
    .filter(s => s.items.length > 0);
});

const carregarPermissoes = async () => {
  const perfilIds = authStore.getPerfilIds;
  if (perfilIds && perfilIds.length) {
    await permissaoStore.carregarMenu(perfilIds);
  }
};

onMounted(() => {
  carregarPermissoes();
  setTimeout(abrirSecaoAtiva, 100)
});

watch(() => authStore.token, () => {
  carregarPermissoes();
});

watch(() => route.path, () => {
  abrirSecaoAtiva()
});

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("aside", {
    class: _normalizeClass(["sidebar", { collapsed: props.collapsed, open: props.open }])
  }, [
    _createElementVNode("div", _hoisted_1, [
      (props.open)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _cache[2] || (_cache[2] = _createElementVNode("span", { class: "sidebar-mobile-title" }, "Menu", -1)),
            _createElementVNode("button", {
              class: "sidebar-close-btn",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('close')))
            }, _cache[1] || (_cache[1] = [
              _createElementVNode("i", { class: "fas fa-times" }, null, -1)
            ]))
          ]))
        : _createCommentVNode("", true),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(secoesVisiveis.value, (secao) => {
        return (_openBlock(), _createElementBlock("div", {
          key: secao.id,
          class: "menu-section"
        }, [
          _createElementVNode("div", {
            class: _normalizeClass(["section-header", { 'section-open': secoesAbertas[secao.id] }]),
            onClick: ($event: any) => (toggleSecao(secao.id))
          }, [
            _createElementVNode("i", {
              class: _normalizeClass(secao.icon)
            }, null, 2),
            _createElementVNode("span", null, _toDisplayString(secao.i18nKey ? _unref(t)(secao.i18nKey) : secao.label), 1),
            _createElementVNode("i", {
              class: _normalizeClass(["fas fa-chevron-down section-chevron", { rotated: secoesAbertas[secao.id] }])
            }, null, 2)
          ], 10, _hoisted_3),
          _withDirectives(_createElementVNode("ul", _hoisted_4, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(secao.items, (item) => {
              return (_openBlock(), _createElementBlock("li", {
                key: item.path
              }, [
                _createVNode(_component_router_link, {
                  to: item.path,
                  "active-class": "active",
                  class: "sidebar-link",
                  onClick: onLinkClick
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("i", {
                      class: _normalizeClass(item.icon)
                    }, null, 2),
                    _createElementVNode("span", null, _toDisplayString(item.i18nKey ? _unref(t)(item.i18nKey) : item.nome), 1)
                  ]),
                  _: 2
                }, 1032, ["to"])
              ]))
            }), 128))
          ], 512), [
            [_vShow, secoesAbertas[secao.id]]
          ])
        ]))
      }), 128))
    ])
  ], 2))
}
}

})